/**
 * API Endpoints Configuration
 * Centralized collection of all API endpoints
 */

export const API_ENDPOINTS = {
  // Auth endpoints (versioned at v1)
  AUTH: {
    LOGIN: '/v1/auth/login',
    LOGOUT: '/v1/auth/logout',
    REFRESH: '/v1/auth/refresh',
    CHANGE_PASSWORD: '/v1/auth/change-password',
    FORGOT_PASSWORD: '/v1/auth/forgot-password',
    RESET_PASSWORD: '/v1/auth/reset-password',
    ME: '/v1/auth/me',
    VERIFY_SIGNUP_OTP: '/v1/auth/verify-otp',
    RESEND_SIGNUP_OTP: '/v1/auth/resend-otp',
    TWO_FACTOR_REQUEST_OTP: '/v1/auth/2fa/request-otp',
    TWO_FACTOR_LOGIN: '/v1/auth/2fa/login',
    TWO_FACTOR_STATUS: '/v1/auth/2fa/status',
    TWO_FACTOR_SETUP_INITIATE: '/v1/auth/2fa/setup/initiate',
    TWO_FACTOR_SETUP_VERIFY: '/v1/auth/2fa/setup/verify',
    TWO_FACTOR_DISABLE: '/v1/auth/2fa/disable',
    CHANGE_EMAIL: '/v1/auth/change-email',
    CONFIRM_EMAIL_CHANGE: '/v1/auth/confirm-email-change',
    MY_EMAILS: '/v1/me/emails',
    MERGE_START: '/v1/me/merge/start',
    MERGE_COMPLETE: '/v1/me/merge/complete',
    DELETE_ACCOUNT: '/v1/auth/account',
    ARCHIVE_ACCOUNT: '/v1/auth/account/archive',
    RESTORE_ACCOUNT: '/v1/auth/account/restore',
    EXPORT_ACCOUNT: '/v1/auth/account/export',
    SSO_PROVIDERS: '/v1/auth/sso/providers',
    SSO_COLLEGE: (slug: string) => `/v1/auth/sso/college/${slug}`,
    SSO_START: (provider: string) => `/v1/auth/sso/${provider.toLowerCase()}/start`,
    SESSIONS: '/v1/auth/sessions',
    SESSIONS_REVOKE_OTHERS: '/v1/auth/sessions/revoke-others',
    SESSION_BY_ID: (id: string) => `/v1/auth/sessions/${id}`,
  },

  // Users endpoints (versioned at v1)
  USERS: {
    LIST: '/v1/users',
    GET: (id: string) => `/v1/users/${id}`,
    CREATE: '/v1/users',
    UPDATE: (id: string) => `/v1/users/${id}`,
    DELETE: (id: string) => `/v1/users/${id}`,
    PROMOTE: (id: string) => `/v1/users/${id}/promote`,
    DEMOTE: (id: string) => `/v1/users/${id}/demote`,
    ACTIVATE: (id: string) => `/v1/users/${id}/activate`,
    SUSPEND: (id: string) => `/v1/users/${id}/suspend`,
    DEACTIVATE: (id: string) => `/v1/users/${id}/deactivate`,
    STATS: '/v1/users/stats',
  },
  // Public profile (no auth required)
  PUBLIC_PROFILE: (id: string) => `/v1/profiles/${id}`,

  // Career Profile endpoints (v1)
  PROFILE: {
    GET: '/v1/profile',
    UPSERT: '/v1/profile',
    HANDLE: '/v1/profile/handle',
    WIZARD_STEP: '/v1/profile/wizard/step',
    WIZARD_COMPLETE: '/v1/profile/wizard/complete',
    COMPLETION: '/v1/profile/completion',
    AVATAR_UPLOAD: '/v1/profile/avatar',
    // Experiences
    ADD_EXPERIENCE: '/v1/profile/experiences',
    UPDATE_EXPERIENCE: (id: string) => `/v1/profile/experiences/${id}`,
    DELETE_EXPERIENCE: (id: string) => `/v1/profile/experiences/${id}`,
    // Education
    ADD_EDUCATION: '/v1/profile/education',
    UPDATE_EDUCATION: (id: string) => `/v1/profile/education/${id}`,
    DELETE_EDUCATION: (id: string) => `/v1/profile/education/${id}`,
    // Projects
    ADD_PROJECT: '/v1/profile/projects',
    UPDATE_PROJECT: (id: string) => `/v1/profile/projects/${id}`,
    DELETE_PROJECT: (id: string) => `/v1/profile/projects/${id}`,
    // Certifications
    ADD_CERTIFICATION: '/v1/profile/certifications',
    UPDATE_CERTIFICATION: (id: string) => `/v1/profile/certifications/${id}`,
    DELETE_CERTIFICATION: (id: string) => `/v1/profile/certifications/${id}`,
    // Languages
    ADD_LANGUAGE: '/v1/profile/languages',
    UPDATE_LANGUAGE: (id: string) => `/v1/profile/languages/${id}`,
    DELETE_LANGUAGE: (id: string) => `/v1/profile/languages/${id}`,
    // Skills — legacy freeform list, kept only for reconciling not-yet-migrated entries
    ADD_SKILL: '/v1/profile/skills',
    REPLACE_SKILLS: '/v1/profile/skills',
    UPDATE_SKILL: (id: string) => `/v1/profile/skills/${id}`,
    DELETE_SKILL: (id: string) => `/v1/profile/skills/${id}`,
  },

  // Skill catalog + a user's catalog-linked skills (self-declared, endorsed, verified) —
  // this is the canonical resume/profile skill list going forward.
  SKILLS: {
    BROWSE: '/v1/skills',
    MY_SKILLS: '/v1/profile/verified-skills',
    ADD_MY_SKILL: '/v1/profile/verified-skills',
    UPDATE_MY_SKILL: (userSkillId: string) => `/v1/profile/verified-skills/${userSkillId}`,
    DELETE_MY_SKILL: (userSkillId: string) => `/v1/profile/verified-skills/${userSkillId}`,
    REORDER_MY_SKILLS: '/v1/profile/verified-skills/reorder',
    USER_SKILLS: (userId: string) => `/v1/users/${userId}/verified-skills`,
    ENDORSE: (userId: string, userSkillId: string) =>
      `/v1/users/${userId}/verified-skills/${userSkillId}/endorse`,
    MY_SKILL_RECOGNIZERS: (userSkillId: string) =>
      `/v1/profile/verified-skills/${userSkillId}/recognizers`,
    REQUEST_MY_SKILL_RECOGNITION: (userSkillId: string) =>
      `/v1/profile/verified-skills/${userSkillId}/recognition-requests`,
  },

  // Organizations
  ORGANIZATIONS: {
    SEARCH: '/v1/organizations/search',
    MY: '/v1/organizations/my',
    GET: (id: string) => `/v1/organizations/${id}`,
    GET_BY_SLUG: (slug: string) => `/v1/organizations/slug/${slug}`,
    CREATE: '/v1/organizations',
    UPDATE: (id: string) => `/v1/organizations/${id}`,
    LOGO: (id: string) => `/v1/organizations/${id}/logo`,
    UPDATE_COMPANY_PROFILE: (id: string) => `/v1/organizations/${id}/company-profile`,
    UPDATE_COLLEGE_PROFILE: (id: string) => `/v1/organizations/${id}/college-profile`,
    // Members
    GET_MEMBERS: (id: string) => `/v1/organizations/${id}/members`,
    INVITE_MEMBER: (id: string) => `/v1/organizations/${id}/members/invite`,
    JOIN: (id: string) => `/v1/organizations/${id}/members/join`,
    GET_PENDING: (id: string) => `/v1/organizations/${id}/members/pending`,
    TRANSFER_OWNERSHIP: (id: string) => `/v1/organizations/${id}/transfer-ownership`,
    APPROVE_MEMBER: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}/approve`,
    REJECT_MEMBER: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}/reject`,
    UPDATE_MEMBER_ROLE: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}/role`,
    REMOVE_MEMBER: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}`,
    LEAVE: (id: string) => `/v1/organizations/${id}/leave`,
    // Roster import / export
    IMPORT_MEMBERS_PREVIEW: (id: string) => `/v1/organizations/${id}/members/import/preview`,
    IMPORT_MEMBERS: (id: string) => `/v1/organizations/${id}/members/import`,
    IMPORT_TEMPLATE: (id: string) => `/v1/organizations/${id}/members/import/template`,
    EXPORT_MEMBERS: (id: string) => `/v1/organizations/${id}/members/export`,
    RESEND_INVITE: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}/resend-invite`,
    REVOKE_INVITE: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/members/${userId}/invite`,
    // Invite acceptance (public — no session yet)
    GET_INVITE: (token: string) => `/v1/invites/${token}`,
    ACCEPT_INVITE: (token: string) => `/v1/invites/${token}/accept`,
    INVITE_SIGNUP: (token: string) => `/v1/invites/${token}/signup`,
    INVITE_SIGNUP_VERIFY: (token: string) => `/v1/invites/${token}/signup/verify`,
    CLAIM_INVITE: (token: string) => `/v1/invites/${token}/claim`,
    // The signed-in invitee's own invitations
    MY_INVITATIONS: '/v1/me/invitations',
    ACCEPT_MY_INVITATION: (orgId: string) => `/v1/me/invitations/${orgId}/accept`,
    DECLINE_MY_INVITATION: (orgId: string) => `/v1/me/invitations/${orgId}/decline`,
    // Departments
    GET_DEPARTMENTS: (id: string) => `/v1/organizations/${id}/departments`,
    CREATE_DEPARTMENT: (id: string) => `/v1/organizations/${id}/departments`,
    UPDATE_DEPARTMENT: (orgId: string, deptId: string) =>
      `/v1/organizations/${orgId}/departments/${deptId}`,
    DELETE_DEPARTMENT: (orgId: string, deptId: string) =>
      `/v1/organizations/${orgId}/departments/${deptId}`,
    // Roles
    GET_ROLES: (id: string) => `/v1/organizations/${id}/roles`,
    CREATE_ROLE: (id: string) => `/v1/organizations/${id}/roles`,
    UPDATE_ROLE: (orgId: string, roleId: string) => `/v1/organizations/${orgId}/roles/${roleId}`,
    DELETE_ROLE: (orgId: string, roleId: string) => `/v1/organizations/${orgId}/roles/${roleId}`,
  },

  // Jobs
  JOBS: {
    BROWSE: '/v1/jobs',
    GET: (id: string) => `/v1/jobs/${id}`,
    SAVED: '/v1/jobs/saved',
    SAVE: (id: string) => `/v1/jobs/${id}/save`,
    UNSAVE: (id: string) => `/v1/jobs/${id}/save`,
    // Org job management
    ORG_LIST: (orgId: string) => `/v1/organizations/${orgId}/jobs`,
    ORG_CREATE: (orgId: string) => `/v1/organizations/${orgId}/jobs`,
    ORG_UPDATE: (orgId: string, jobId: string) => `/v1/organizations/${orgId}/jobs/${jobId}`,
    ORG_DELETE: (orgId: string, jobId: string) => `/v1/organizations/${orgId}/jobs/${jobId}`,
    PUBLISH: (orgId: string, jobId: string) => `/v1/organizations/${orgId}/jobs/${jobId}/publish`,
    CLOSE: (orgId: string, jobId: string) => `/v1/organizations/${orgId}/jobs/${jobId}/close`,
    ARCHIVE: (orgId: string, jobId: string) => `/v1/organizations/${orgId}/jobs/${jobId}/archive`,
    PIPELINE_STAGES: (orgId: string, jobId: string) =>
      `/v1/organizations/${orgId}/jobs/${jobId}/pipeline-stages`,
  },

  // Applications
  APPLICATIONS: {
    APPLY: (jobId: string) => `/v1/jobs/${jobId}/apply`,
    MY_LIST: '/v1/applications',
    MY_GET: (id: string) => `/v1/applications/${id}`,
    WITHDRAW: (id: string) => `/v1/applications/${id}/withdraw`,
    REVOKE_RESUME: (id: string) => `/v1/applications/${id}/revoke-resume`,
    MY_NOTES: (id: string) => `/v1/applications/${id}/notes`,
    // Org application management
    ORG_LIST: (orgId: string) => `/v1/organizations/${orgId}/applications`,
    ORG_GET: (orgId: string, id: string) => `/v1/organizations/${orgId}/applications/${id}`,
    ORG_UPDATE_STATUS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/status`,
    ORG_MOVE_STAGE: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/stage`,
    ORG_BULK: (orgId: string) => `/v1/organizations/${orgId}/applications/bulk`,
    ORG_ADD_NOTE: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/notes`,
    ORG_GET_NOTES: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/notes`,
    ORG_SUMMARY: (orgId: string) => `/v1/organizations/${orgId}/applications/summary`,
    ORG_OFFER: (orgId: string, id: string) => `/v1/organizations/${orgId}/applications/${id}/offer`,
    ORG_RESUME: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/resume`,
    ORG_CONVERSATION: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/applications/${id}/conversation`,
    DRAFT: (jobId: string) => `/v1/jobs/${jobId}/application-draft`,
  },

  // Interviews
  INTERVIEWS: {
    ORG_LIST: (orgId: string) => `/v1/organizations/${orgId}/interviews`,
    FOR_APPLICATION: (orgId: string, applicationId: string) =>
      `/v1/organizations/${orgId}/applications/${applicationId}/interviews`,
    UPDATE: (orgId: string, interviewId: string) =>
      `/v1/organizations/${orgId}/interviews/${interviewId}`,
    FEEDBACK: (orgId: string, interviewId: string) =>
      `/v1/organizations/${orgId}/interviews/${interviewId}/feedback`,
  },

  // Resumes
  RESUMES: {
    LIST: '/v1/resumes',
    CREATE: '/v1/resumes',
    UPLOAD: '/v1/resumes/upload',
    ENHANCE_TEXT: '/v1/resumes/enhance-text',
    REVIEW_TEXT: '/v1/resumes/review-text',
    DRAFT_BULLETS: '/v1/resumes/draft-bullets',
    TAILOR_BULLETS: '/v1/resumes/tailor-bullets',
    GET: (id: string) => `/v1/resumes/${id}`,
    UPDATE: (id: string) => `/v1/resumes/${id}`,
    DELETE: (id: string) => `/v1/resumes/${id}`,
    SAVE_CONTENT: (id: string) => `/v1/resumes/${id}/content`,
    UPLOAD_PHOTO: (id: string) => `/v1/resumes/${id}/photo`,
    SET_DEFAULT: (id: string) => `/v1/resumes/${id}/default`,
    GET_VERSIONS: (id: string) => `/v1/resumes/${id}/versions`,
    RESTORE_VERSION: (id: string, versionNumber: number) =>
      `/v1/resumes/${id}/versions/${versionNumber}/restore`,
    GET_SHARE_LINKS: (id: string) => `/v1/resumes/${id}/share-links`,
    CREATE_SHARE_LINK: (id: string) => `/v1/resumes/${id}/share-links`,
    REVOKE_SHARE_LINK: (resumeId: string, linkId: string) =>
      `/v1/resumes/${resumeId}/share-links/${linkId}`,
    SHARED: (token: string) => `/v1/resumes/shared/${token}`,
    EXPORT_DOCX: (id: string) => `/v1/resumes/${id}/export/docx`,
    ATS_READOUT: (id: string) => `/v1/resumes/${id}/export/ats-text`,
    PROFILE_SNAPSHOT: '/v1/resumes/profile-snapshot',
    STYLE_PRESET: '/v1/resumes/style-preset',
    EXPORTS: (id: string) => `/v1/resumes/${id}/exports`,
    EXPORT_PDF: (id: string) => `/v1/resumes/${id}/export/pdf`,
    DUPLICATE: (id: string) => `/v1/resumes/${id}/duplicate`,
  },

  // Cover Letters
  COVER_LETTERS: {
    LIST: '/v1/cover-letters',
    CREATE: '/v1/cover-letters',
    GENERATE: '/v1/cover-letters/generate',
    GET: (id: string) => `/v1/cover-letters/${id}`,
    UPDATE: (id: string) => `/v1/cover-letters/${id}`,
    DELETE: (id: string) => `/v1/cover-letters/${id}`,
    GET_VERSIONS: (id: string) => `/v1/cover-letters/${id}/versions`,
    EXPORT_PDF: (id: string) => `/v1/cover-letters/${id}/export/pdf`,
    RESTORE_VERSION: (id: string, versionNumber: number) =>
      `/v1/cover-letters/${id}/versions/${versionNumber}/restore`,
  },

  // Documents
  DOCUMENTS: {
    LIST: '/v1/documents',
    CREATE: '/v1/documents',
    GET: (id: string) => `/v1/documents/${id}`,
    UPDATE: (id: string) => `/v1/documents/${id}`,
    DELETE: (id: string) => `/v1/documents/${id}`,
    GET_SHARE_LINKS: (id: string) => `/v1/documents/${id}/share-links`,
    CREATE_SHARE_LINK: (id: string) => `/v1/documents/${id}/share-links`,
    REVOKE_SHARE_LINK: (docId: string, linkId: string) =>
      `/v1/documents/${docId}/share-links/${linkId}`,
    GET_SHARED: (token: string) => `/v1/documents/shared/${token}`,
  },

  // Placement Drives (college workspace)
  PLACEMENT_DRIVES: {
    LIST: (orgId: string) => `/v1/organizations/${orgId}/placement-drives`,
    CREATE: (orgId: string) => `/v1/organizations/${orgId}/placement-drives`,
    GET: (orgId: string, id: string) => `/v1/organizations/${orgId}/placement-drives/${id}`,
    UPDATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/placement-drives/${id}`,
    ROUNDS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/placement-drives/${id}/rounds`,
    REGISTRATIONS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/placement-drives/${id}/registrations`,
    ROUND_RESULTS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/placement-drives/${id}/round-results`,
    RECORD_OFFER: (orgId: string, id: string, registrationId: string) =>
      `/v1/organizations/${orgId}/placement-drives/${id}/registrations/${registrationId}/offer`,
    ELIGIBILITY_SUMMARY: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/placement-drives/${id}/eligibility-summary`,
  },

  // Campus placement (student side, offers, opt-outs, college dashboard)
  CAMPUS: {
    MY_DRIVES: '/v1/me/placement-drives',
    MY_DRIVE: (driveId: string) => `/v1/me/placement-drives/${driveId}`,
    REGISTER: (driveId: string) => `/v1/me/placement-drives/${driveId}/register`,
    WITHDRAW: (driveId: string) => `/v1/me/placement-drives/${driveId}/withdraw`,
    MY_OFFERS: '/v1/me/offers',
    RESPOND_OFFER: (offerId: string) => `/v1/me/offers/${offerId}/respond`,
    MY_OPT_OUTS: '/v1/me/opt-outs',
    WITHDRAW_OPT_OUT: (id: string) => `/v1/me/opt-outs/${id}`,
    REQUEST_REJOIN: (id: string) => `/v1/me/opt-outs/${id}/rejoin`,
    REQUEST_OPT_OUT: (orgId: string) => `/v1/organizations/${orgId}/opt-outs`,
    ORG_OPT_OUTS: (orgId: string) => `/v1/organizations/${orgId}/opt-outs`,
    REVIEW_OPT_OUT: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/opt-outs/${id}/review`,
    REVIEW_REJOIN: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/opt-outs/${id}/rejoin`,
    CORRECT_PLACEMENT: (orgId: string, userId: string) =>
      `/v1/organizations/${orgId}/students/${userId}/placement`,
    DASHBOARD: (orgId: string) => `/v1/organizations/${orgId}/college-dashboard`,
    ANALYTICS: (orgId: string) => `/v1/organizations/${orgId}/placement-analytics`,
  },

  // College structure, batches and self-serve join links
  ACADEMIC: {
    STRUCTURE: (orgId: string) => `/v1/organizations/${orgId}/structure`,
    ORG_UNITS: (orgId: string) => `/v1/organizations/${orgId}/org-units`,
    ORG_UNIT: (orgId: string, unitId: string) => `/v1/organizations/${orgId}/org-units/${unitId}`,
    DEPARTMENT_UNIT: (orgId: string, deptId: string) =>
      `/v1/organizations/${orgId}/departments/${deptId}/unit`,
    COURSES: (orgId: string, deptId: string) =>
      `/v1/organizations/${orgId}/departments/${deptId}/courses`,
    COURSE: (orgId: string, courseId: string) => `/v1/organizations/${orgId}/courses/${courseId}`,
    COURSE_BATCHES: (orgId: string, courseId: string) =>
      `/v1/organizations/${orgId}/courses/${courseId}/batches`,
    BATCHES: (orgId: string) => `/v1/organizations/${orgId}/batches`,
    BATCH: (orgId: string, batchId: string) => `/v1/organizations/${orgId}/batches/${batchId}`,
    BATCH_STUDENTS: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/students`,
    BATCH_STUDENT: (orgId: string, batchId: string, userId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/students/${userId}`,
    STAFF_ASSIGNMENTS: (orgId: string) => `/v1/organizations/${orgId}/staff-assignments`,
    BATCH_STAFF: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/staff`,
    BATCH_STAFF_MEMBER: (orgId: string, batchId: string, userId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/staff/${userId}`,
    JOIN_LINKS: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/join-links`,
    JOIN_LINK: (orgId: string, linkId: string) => `/v1/organizations/${orgId}/join-links/${linkId}`,
    MY_ENROLLMENTS: (orgId: string) => `/v1/organizations/${orgId}/my-enrollments`,
    MY_VISIBILITY: (orgId: string) => `/v1/organizations/${orgId}/my-enrollment/visibility`,
    SETUP: (orgId: string) => `/v1/organizations/${orgId}/setup`,
    SETUP_COMPLETE: (orgId: string) => `/v1/organizations/${orgId}/setup/complete`,
    JOIN: (code: string) => `/v1/join/${code}`,
  },

  // College uploads (academics, external test scores) and batch rankings
  RECORDS: {
    TEMPLATE: (orgId: string, kind: string) =>
      `/v1/organizations/${orgId}/records/templates/${kind}`,
    PREVIEW: (orgId: string, kind: string) => `/v1/organizations/${orgId}/records/${kind}/preview`,
    COMMIT: (orgId: string, kind: string) => `/v1/organizations/${orgId}/records/${kind}`,
    EXTERNAL_TESTS: (orgId: string) => `/v1/organizations/${orgId}/external-tests`,
    SKILL_REPORT: (orgId: string) => `/v1/organizations/${orgId}/skill-report`,
    CAREER_PROFILES: (orgId: string) => `/v1/organizations/${orgId}/career-profiles`,
    RECRUITER_CONTACTS: (orgId: string) => `/v1/organizations/${orgId}/recruiter-contacts`,
    RECRUITER_CONTACT: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/recruiter-contacts/${id}`,
    DOCUMENT_REQUESTS: (orgId: string) => `/v1/organizations/${orgId}/document-requests`,
    DOCUMENT_REQUEST: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/document-requests/${id}`,
    DOCUMENT_SUBMISSION_REVIEW: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/document-submissions/${id}/review`,
    DOCUMENT_SUBMISSION_FILE: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/document-submissions/${id}/file`,
    MY_DOCUMENT_REQUESTS: '/v1/me/document-requests',
    RESUME_REVIEWS: (orgId: string) => `/v1/organizations/${orgId}/resume-reviews`,
    RESUME_REVIEW: (orgId: string, id: string) => `/v1/organizations/${orgId}/resume-reviews/${id}`,
    MY_RESUME_REVIEWS: '/v1/me/resume-reviews',
    ASSIGNABLE_ASSESSMENTS: (orgId: string) => `/v1/organizations/${orgId}/assignable-assessments`,
    ASSESSMENT_ASSIGNMENTS: (orgId: string) => `/v1/organizations/${orgId}/assessment-assignments`,
    ASSESSMENT_ASSIGNMENT: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/assessment-assignments/${id}`,
    MY_ASSESSMENT_ASSIGNMENTS: '/v1/me/assessment-assignments',
    NOTIFICATION_PREFERENCES: '/v1/me/notification-preferences',
    MY_RESUME_REVIEW_CANCEL: (id: string) => `/v1/me/resume-reviews/${id}/cancel`,
    MY_DOCUMENT_SUBMISSION: (requestId: string) =>
      `/v1/me/document-requests/${requestId}/submission`,
    BATCH_TESTS: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/tests`,
    LEADERBOARD: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/leaderboard`,
    SKILL_INSIGHTS: (orgId: string, batchId: string) =>
      `/v1/organizations/${orgId}/batches/${batchId}/skill-insights`,
    MY_RANKS: '/v1/me/skill-ranks',
    MY_BADGES: '/v1/me/rank-badges',
    PROFILE_BADGES: (profileId: string) => `/v1/profiles/${profileId}/rank-badges`,
  },

  // Personal dashboard (one aggregated call)
  DASHBOARD: {
    MINE: '/v1/me/dashboard',
  },

  // College noticeboard
  NOTICES: {
    MINE: '/v1/me/notices',
    ORG: (orgId: string) => `/v1/organizations/${orgId}/notices`,
    PIN: (orgId: string, id: string) => `/v1/organizations/${orgId}/notices/${id}/pin`,
    DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/notices/${id}`,
  },

  // Job ↔ profile matching and recruiter screening
  JOB_MATCH: {
    MINE: (jobId: string) => `/v1/jobs/${jobId}/match`,
    SCORES: '/v1/job-match-scores',
    CRITERIA: (orgId: string, jobId: string) =>
      `/v1/organizations/${orgId}/jobs/${jobId}/match-criteria`,
    CANDIDATES: (orgId: string, jobId: string) =>
      `/v1/organizations/${orgId}/jobs/${jobId}/candidate-matches`,
  },

  // AI Insights
  AI: {
    INSIGHTS: '/v1/ai/insights',
    INSIGHT: (id: string) => `/v1/ai/insights/${id}`,
    TRIGGER_RESUME_READINESS: '/v1/ai/insights/resume-readiness',
    TRIGGER_JOB_MATCH: '/v1/ai/insights/job-match',
    TRIGGER_SKILL_GAP: '/v1/ai/insights/skill-gap',
    TRIGGER_RECOMMENDATIONS: '/v1/ai/insights/recommendations',
  },

  // Assessments
  ASSESSMENTS: {
    BROWSE: '/v1/assessments',
    GET: (id: string) => `/v1/assessments/${id}`,
    START_ATTEMPT: (id: string) => `/v1/assessments/${id}/attempt`,
    GET_ATTEMPT: (id: string) => `/v1/assessments/attempts/${id}`,
    GET_ATTEMPT_QUESTIONS: (id: string) => `/v1/assessments/attempts/${id}/questions`,
    SUBMIT_ATTEMPT: (id: string) => `/v1/assessments/attempts/${id}/submit`,
    CERTIFICATE: (id: string) => `/v1/assessments/attempts/${id}/certificate`,
    MY_ATTEMPTS: '/v1/assessments/my-attempts',
  },

  // Messages & Conversations
  MESSAGES: {
    LIST_CONVERSATIONS: '/v1/conversations',
    START_CONVERSATION: '/v1/conversations',
    GET_CONVERSATION: (id: string) => `/v1/conversations/${id}`,
    GET_MESSAGES: (id: string) => `/v1/conversations/${id}/messages`,
    SEND_MESSAGE: (id: string) => `/v1/conversations/${id}/messages`,
    MARK_READ: (id: string) => `/v1/conversations/${id}/read`,
    SEND_ATTACHMENT: (id: string) => `/v1/conversations/${id}/attachments`,
    DOWNLOAD_ATTACHMENT: (attachmentId: string) => `/v1/message-attachments/${attachmentId}`,
    PINS: (id: string) => `/v1/conversations/${id}/pins`,
    PEER: (id: string) => `/v1/conversations/${id}/peer`,
    STARRED: '/v1/messages/starred',
    MESSAGE: (messageId: string) => `/v1/messages/${messageId}`,
    REACTION: (messageId: string) => `/v1/messages/${messageId}/reaction`,
    PIN: (messageId: string) => `/v1/messages/${messageId}/pin`,
    STAR: (messageId: string) => `/v1/messages/${messageId}/star`,
    FORWARD: (messageId: string) => `/v1/messages/${messageId}/forward`,
    INFO: (messageId: string) => `/v1/messages/${messageId}/info`,
    SHARED: (id: string) => `/v1/conversations/${id}/shared`,
    LINK_PREVIEW: '/v1/link-preview',
    SEARCH: '/v1/messages/search',
    PRIVACY: '/v1/messaging/privacy',
    COMPANY_CONVERSATIONS: (orgId: string) =>
      `/v1/organizations/${orgId}/company-messages/conversations`,
    COMPANY_CAPABILITIES: (orgId: string) =>
      `/v1/organizations/${orgId}/company-messages/capabilities`,
    INTERVIEW_SLOTS: (id: string) => `/v1/conversations/${id}/interview-slots`,
    INTERVIEW_SLOT_PICK: (messageId: string) => `/v1/messages/${messageId}/interview-slots/pick`,
    INTERVIEW_SLOT_CANCEL: (messageId: string) =>
      `/v1/messages/${messageId}/interview-slots/cancel`,
    READ_ALL: '/v1/conversations/read-all',
    CONVERSATION_MUTE: (id: string) => `/v1/conversations/${id}/mute`,
    CONVERSATION_PIN: (id: string) => `/v1/conversations/${id}/pin`,
    CONVERSATION_ARCHIVE: (id: string) => `/v1/conversations/${id}/archive`,
    CONVERSATION_UNREAD: (id: string) => `/v1/conversations/${id}/unread`,
    CONVERSATION_HISTORY: (id: string) => `/v1/conversations/${id}/history`,
    CONVERSATION_BLOCK: (id: string) => `/v1/conversations/${id}/block`,
    CONVERSATION_REPORT: (id: string) => `/v1/conversations/${id}/report`,
    // A college's Messages page: the staff member's college inbox and recipient picker.
    // Threads themselves use the endpoints above.
    COLLEGE_CONVERSATIONS: (orgId: string) => `/v1/organizations/${orgId}/messages/conversations`,
    COLLEGE_RECIPIENTS: (orgId: string) => `/v1/organizations/${orgId}/messages/recipients`,
  },

  // Connections
  CONNECTIONS: {
    LIST: '/v1/connections',
    REMOVE: (id: string) => `/v1/connections/${id}`,
    FAVORITE: (id: string) => `/v1/connections/${id}/favorite`,
    REQUESTS_RECEIVED: '/v1/connections/requests/received',
    REQUESTS_SENT: '/v1/connections/requests/sent',
    SEND_REQUEST: '/v1/connections/requests',
    ACCEPT_REQUEST: (id: string) => `/v1/connections/requests/${id}/accept`,
    DECLINE_REQUEST: (id: string) => `/v1/connections/requests/${id}/decline`,
    CANCEL_REQUEST: (id: string) => `/v1/connections/requests/${id}`,
    STATUS: (userId: string) => `/v1/connections/status/${userId}`,
    MUTUAL: (userId: string) => `/v1/connections/mutual/${userId}`,
    DISCOVER: '/v1/connections/discover',
    SUGGESTIONS: '/v1/connections/suggestions',
    BLOCKED: '/v1/connections/blocked',
    BLOCK: (userId: string) => `/v1/connections/blocked/${userId}`,
    UNBLOCK: (userId: string) => `/v1/connections/blocked/${userId}`,
  },

  // Notifications
  // Location master data (public — typeahead + reference dropdowns)
  LOCATIONS: {
    SEARCH: '/v1/locations/search',
    COUNTRIES: '/v1/locations/countries',
    STATES: '/v1/locations/states',
  },

  NOTIFICATIONS: {
    LIST: '/v1/notifications',
    UNREAD_COUNT: '/v1/notifications/unread-count',
    MARK_READ: '/v1/notifications/mark-read',
    MARK_ALL_READ: '/v1/notifications/mark-all-read',
  },

  // Billing (customer-facing, v1)
  BILLING: {
    PLANS: '/v1/billing/plans',
    OVERVIEW: '/v1/billing/overview',
    INVOICES: '/v1/billing/invoices',
    CHECKOUT: '/v1/billing/checkout',
    RECONCILE: '/v1/billing/checkout/reconcile',
    CANCEL: '/v1/billing/subscription/cancel',
    RESUME: '/v1/billing/subscription/resume',
    CHANGE_PLAN: '/v1/billing/subscription/change-plan',
    CHANGE_PLAN_PREVIEW: '/v1/billing/subscription/change-plan/preview',
    ENTITLEMENT_CHECK: '/v1/billing/entitlements/check',
    COUPON_PREVIEW: '/v1/billing/coupons/preview',
    PORTAL: '/v1/billing/portal',
  },
  // Admin endpoints — base path is /admin (not /v1)
  ADMIN: {
    // Stats
    PLATFORM_STATS: '/admin/stats',
    // Users
    USERS_LIST: '/admin/users',
    USER_DETAIL: (id: string) => `/admin/users/${id}`,
    USER_SUSPEND: (id: string) => `/admin/users/${id}/suspend`,
    USER_UNSUSPEND: (id: string) => `/admin/users/${id}/unsuspend`,
    USER_ADJUST_CREDITS: (id: string) => `/admin/users/${id}/credits`,
    USER_DELETE: (id: string) => `/admin/users/${id}`,
    // Organizations
    ORGS_LIST: '/admin/organizations',
    ORG_DETAIL: (id: string) => `/admin/organizations/${id}`,
    ORG_PENDING: '/admin/organizations/pending',
    ORG_APPROVE: (id: string) => `/admin/organizations/${id}/approve`,
    ORG_REJECT: (id: string) => `/admin/organizations/${id}/reject`,
    ORG_SUSPEND: (id: string) => `/admin/organizations/${id}/suspend`,
    // Jobs
    JOBS_LIST: '/admin/jobs',
    JOB_STATS: '/admin/jobs/stats',
    // Assessments (platform authoring + org oversight)
    ASSESSMENTS_LIST: '/admin/assessments',
    ASSESSMENT_CREATE: '/admin/assessments',
    ASSESSMENTS_OVERSIGHT: '/admin/assessments/oversight',
    ASSESSMENT_DETAIL: (id: string) => `/admin/assessments/${id}`,
    ASSESSMENT_UPDATE: (id: string) => `/admin/assessments/${id}`,
    ASSESSMENT_QUESTIONS: (id: string) => `/admin/assessments/${id}/questions`,
    ASSESSMENT_PUBLISH: (id: string) => `/admin/assessments/${id}/publish`,
    ASSESSMENT_ARCHIVE: (id: string) => `/admin/assessments/${id}/archive`,
    ASSESSMENT_UNPUBLISH: (id: string) => `/admin/assessments/${id}/unpublish`,
    // Master data (generic: view/edit/delete/bulk-upload for registered catalog entities)
    MASTER_DATA_LIST: (entity: string) => `/admin/master-data/${entity}`,
    MASTER_DATA_GET: (entity: string, id: string) => `/admin/master-data/${entity}/${id}`,
    MASTER_DATA_CREATE: (entity: string) => `/admin/master-data/${entity}`,
    MASTER_DATA_UPDATE: (entity: string, id: string) => `/admin/master-data/${entity}/${id}`,
    MASTER_DATA_DELETE: (entity: string, id: string) => `/admin/master-data/${entity}/${id}`,
    MASTER_DATA_REFERENCES: (entity: string, id: string) =>
      `/admin/master-data/${entity}/${id}/references`,
    MASTER_DATA_BULK_UPLOAD: (entity: string) => `/admin/master-data/${entity}/bulk`,
    // Credits
    CREDIT_CONFIGS: '/admin/credits/config',
    CREDIT_CONFIG_UPDATE: (action: string) => `/admin/credits/config/${action}`,
    CREDIT_USAGE: '/admin/credits/usage',
    // Feature Flags
    FLAGS_LIST: '/admin/feature-flags',
    FLAG_CREATE: '/admin/feature-flags',
    FLAG_TOGGLE: (key: string) => `/admin/feature-flags/${key}/toggle`,
    // Admin Team
    ADMINS_LIST: '/admin/team',
    ADMIN_INVITE: '/admin/team/invite',
    ADMIN_UPDATE_ROLE: (id: string) => `/admin/team/${id}/role`,
    ADMIN_DEACTIVATE: (id: string) => `/admin/team/${id}`,
    // Activity Log
    ACTIVITY_LOG: '/admin/activity',
    // Billing (gated to SUPER_ADMIN | FINANCE_ADMIN)
    BILLING_STATUS: '/admin/billing/status',
    BILLING_SUBSCRIPTIONS: '/admin/billing/subscriptions',
    BILLING_SUBSCRIPTION_DETAIL: (id: string) => `/admin/billing/subscriptions/${id}`,
    BILLING_SUBSCRIPTION_CHANGE_PLAN: (id: string) =>
      `/admin/billing/subscriptions/${id}/change-plan`,
    BILLING_SUBSCRIPTION_CHANGE_PLAN_PREVIEW: (id: string) =>
      `/admin/billing/subscriptions/${id}/change-plan/preview`,
    BILLING_SUBSCRIPTION_PAUSE: (id: string) => `/admin/billing/subscriptions/${id}/pause`,
    BILLING_SUBSCRIPTION_RESUME: (id: string) => `/admin/billing/subscriptions/${id}/resume`,
    BILLING_SUBSCRIPTION_CANCEL: (id: string) => `/admin/billing/subscriptions/${id}/cancel`,
    BILLING_SUBSCRIPTION_EXTEND_TRIAL: (id: string) =>
      `/admin/billing/subscriptions/${id}/extend-trial`,
    BILLING_SUBSCRIPTION_GRANT: '/admin/billing/subscriptions/grant',
    BILLING_INVOICES: '/admin/billing/invoices',
    BILLING_INVOICE_REFUND: (id: string) => `/admin/billing/invoices/${id}/refund`,
    BILLING_INVOICE_MARK_PAID: (id: string) => `/admin/billing/invoices/${id}/mark-paid`,
    BILLING_GST_CONFIG: '/admin/billing/gst/config',
    BILLING_DUNNING_CONFIG: '/admin/billing/dunning/config',
    BILLING_DUNNING_FAILED: '/admin/billing/dunning/failed-payments',
    BILLING_DUNNING_RETRY: (id: string) => `/admin/billing/dunning/${id}/retry`,
    BILLING_DUNNING_PAUSE: (id: string) => `/admin/billing/dunning/${id}/pause`,
    BILLING_DUNNING_RESUME: (id: string) => `/admin/billing/dunning/${id}/resume`,
    BILLING_DUNNING_WRITE_OFF: (id: string) => `/admin/billing/dunning/${id}/write-off`,
    BILLING_PLAN_MATRIX: '/admin/billing/plans/matrix',
    BILLING_PLAN_LIMITS: (id: string) => `/admin/billing/plans/${id}/limits`,
    BILLING_ENTITLEMENTS: '/admin/billing/entitlements',
    BILLING_ENTITLEMENT_GRANT: '/admin/billing/entitlements/grant',
    BILLING_ENTITLEMENT_REVOKE: (id: string) => `/admin/billing/entitlements/${id}/revoke`,
    BILLING_COUPONS: '/admin/billing/coupons',
    BILLING_COUPON_UPDATE: (id: string) => `/admin/billing/coupons/${id}`,
    BILLING_REPORT_SUMMARY: '/admin/billing/reports/summary',
    BILLING_REPORT_COHORTS: '/admin/billing/reports/cohorts',
    // Platform Handbook
    HANDBOOK_EMAILS: '/admin/handbook/emails',
    BILLING_REPORT_AGING: '/admin/billing/reports/aging',
    BILLING_REPORT_EXPORT: '/admin/billing/reports/export',
    BILLING_AUDIT_LOGS: '/admin/billing/audit-logs',
    // Location suggestions (master data review queue)
    LOCATION_SUGGESTIONS: '/admin/locations/suggestions',
    LOCATION_SUGGESTION_APPROVE: (id: number) => `/admin/locations/suggestions/${id}/approve`,
    LOCATION_SUGGESTION_MAP: (id: number) => `/admin/locations/suggestions/${id}/map`,
    LOCATION_SUGGESTION_REJECT: (id: number) => `/admin/locations/suggestions/${id}/reject`,
    // Skill suggestions (master data review queue)
    SKILL_SUGGESTIONS: '/admin/skills/suggestions',
    SKILL_SUGGESTION_APPROVE: (id: string) => `/admin/skills/suggestions/${id}/approve`,
    SKILL_SUGGESTION_MERGE: (id: string) => `/admin/skills/suggestions/${id}/merge`,
    SKILL_SUGGESTION_REJECT: (id: string) => `/admin/skills/suggestions/${id}/reject`,
  },
} as const;

/**
 * Query parameter types for API calls
 */
export interface ListQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface UserListQueryParams extends ListQueryParams {
  role?: string;
  status?: string;
}
